import { IPromise, IQService, IScope } from "angular";
import { StateService } from "@uirouter/angularjs";
import { IModalService, IModalServiceInstance, IModalScope } from "angular-ui-bootstrap";
import { IGoal } from "@gtmhub/goals/models";
import { GoalService } from "@gtmhub/goals/goalService";
import { TaskEvents } from "@gtmhub/tasks";
import { ListService } from "../services/listService";
import { PermissionsService } from "@gtmhub/users/permissionsService";
import { CurrentUserService } from "@gtmhub/users/currentUserService";
import { localize } from "@gtmhub/localization";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import {
  ListOptions,
  SortingOptions,
  ListActions,
  ListPropertiesTypes,
  ListRuleArray,
  ListsPropertyNames,
  ListFilterTypes,
  IListProperty,
  IListFilter,
  ILoadList,
  ListTypeKeysUI,
  IListSort,
  IList,
  DynamicFilters,
  DynamicValueType,
  ListTypes,
  ListTypeKeys,
  IOKRFilterView,
  IListColumn,
  IListPropertyToGridFilterDefinitionContext,
  IGridFilterToListFilterContext,
  IGoalList,
  IMetricHistory,
  ListView,
  listViewDisplayNameMap,
  ListViewDisplayName,
  IListFilters,
  ListChartSettings,
} from "../models/models";
import { ListChangedEvent } from "./lists";
import { IAssignee, IAssigneeFilterView, IAssigneeIdMap } from "@gtmhub/assignees/models";
import { ICollection, IGtmhubRootScopeService } from "@gtmhub/models";
import { AnalyticsService } from "@gtmhub/core/analytics";
import colors from "wwwroot/json/colors.json";
import { IExportListToExcelBody, DocumentExporterService, IListColumnExcel } from "@gtmhub/document-exporter/document-exporter-service";
import { IStateInit } from "@gtmhub/core/routing";
import { INgRedux } from "@gtmhub/state-management";
import { AssigneeActions } from "../../assignees/redux/assignee-actions";
import { ITeamFilterView, ITeamsIdMap } from "@gtmhub/teams";
import { flatten, IdMap, toIdMap, unique } from "@gtmhub/util";
import { MetricService } from "@gtmhub/okrs/metrics";
import { GoalsChangedEventArgs, OkrEvents } from "@gtmhub/okrs";
import { IExtendedRootScopeService } from "../../shared/ng-extensions/models";
import { TeamEvents } from "../../teams/events";
import { BadgeEvents } from "../../badges/badgeEvents";
import {
  IGridFilter,
  IGridSortingDefinition,
  IGridColumnDefinition,
  GridFilterDefinitions,
  GridSortingDefinitions,
  GridColumnDefinitions,
  toGridDefinitionMap,
  IGridFiltersChange,
} from "@gtmhub/shared/components/grid-filtering-bar";
import {
  IAccess,
  IPlanningSession,
  ISessionFilterView,
  IUserItemAllowedActions,
  ISessionsStoreState,
  filterActiveSessions,
  PlanningSessionsActions,
} from "@gtmhub/sessions";
import { IAssigneesStoreState } from "@gtmhub/assignees";
import { convertListPropertyToGridFilterDefinition, convertListFiltersToGridFilters, convertGridFiltersToListFilters, toggleClosedStatusSort } from "../filter-utils";
import { IConfidenceSettingsRootScopeService } from "@gtmhub/configuration/controllers/confidence-settings";
import { FeatureToggleV2Service } from "@gtmhub/featureToggle";
import { triggerFileDownload } from "@gtmhub/shared/dom";
import { getCurrentUserId, getCurrentUserRoles } from "@gtmhub/users";
import { SizeOfGif } from "@gtmhub/shared/services/tenor-gif.service";
import { userHasOnlyResctrictedRole, getLatestSnapshotCustomFieldValue, isCustomFieldColumn } from "./helpers";
import { FeatureFlag } from "@gtmhub/featureToggle/models";
import { ViewHistoryService } from "@gtmhub/view-history";
import { navItemsFactory } from "@webapp/navigation/utils/nav-items.util";
import { ChartSettingsLoader } from "../services/chart-settings-loader";
import { ChartSettingsModalOpener } from "../services/chart-settings-modal-opener";
import { FavoritesManager } from "@gtmhub/favorites/favorites.manager";
import { INavItem } from "@webapp/navigation/models/nav-items-list.models";
import { FavoritesManagerFactory } from "@gtmhub/favorites/favorites.manager.factory";

export interface IListStateParams {
  listId: string;
  isNewList: boolean;
}

export interface IConfirmDeleteScope extends IModalScope {
  confirm(): void;
}

export interface IListScope extends IScope, IExtendedRootScopeService, IScopeFromRedux {
  isCloseOkrFeatureEnabled: boolean;
  isChartsInListsEnabled: boolean;
  sizeOfGif: typeof SizeOfGif;
  screenName: string;
  allowedActions: string[];
  editMode: boolean;
  properties: Record<string, IListProperty>;
  selectableListProperties: IListProperty[];
  goalsIdMap: IdMap<IOKRFilterView>;
  teamsIdMap: ITeamsIdMap;
  supportedTypes: string[];
  ruleDictionary: Record<string, unknown>;
  columnDragStart(index: number): void;
  sortingFieldsInUseArray: ListRuleArray;
  checkedColumnsMap: Record<string, IListColumn>;
  exportToExcel(): void;
  openItem(type: ListTypeKeys, item: unknown): void;
  getLatestSnapshotCustomFieldValue(fieldName: string, latestSnapshot: IMetricHistory): string;
  changeListColor(color: string): void;
  reloadingList: boolean;
  sortingDefinitions: GridSortingDefinitions;
  sortingChange(): void;
  filterDefinitions: GridFilterDefinitions;
  filters: IGridFilter[];
  filtersChange(change: IGridFiltersChange): void;
  columnDefinitions: GridColumnDefinitions;
  columnsChange(): void;
  onChangeListTitle(): void;
  changeListType(type: ListTypes): void;
  cloneList(): void;
  indicators: {
    initialLoad: boolean;
  };
  list: IList;
  view: ListView;
  viewIcon: string;
  viewDisplayName: ListViewDisplayName;
  listItems: unknown[];
  listOptions: typeof ListOptions;
  listActions: typeof ListActions;
  sortingRules: IListSort[];
  sortingOptions: typeof SortingOptions;
  colors: string[];
  columns: IListColumn[];
  navListsIds: string[];
  toggled: {
    filter: boolean;
    columns: boolean;
    sort: boolean;
    permissions: boolean;
  };
  propertiesDictionary: { [fieldName: string]: string };
  confirmDelete(): void;
  onColumnResizeStart($event: DragEvent): void;
  onColumnResizeEnd($event: DragEvent, column: IListColumn): void;
  triggerNewView(actions: ListActions, listOptions?: ListOptions, rules?: IListFilter): void;
  changeColumnOrder(): void;
  saveNewListRules(): void;
  saveListAs(): void;
  currentPage: number;
  totalPages: number;
  onPageChange(page: number): void;
  onAccessChange(access: IAccess): void;
  currentUserRestricted: boolean;
  onViewChange(view: ListView, viewIcon: string): void;
  filtersForChartSettings: IListFilters;
  addToFavorites(): void;
  removeFromFavorites(): void;
  isFavorite(): boolean;
}

interface IScopeFromRedux {
  reduxStatesInitialized: boolean;
  reduxStateError: IUIError;
  sessionIdMap: Record<string, IPlanningSession>;
  assigneesIdMap: IAssigneeIdMap;
  assignees: IAssignee[];
  activeSessionsIds: string[];
}

const bindStateToScope = (state: IAssigneesStoreState & ISessionsStoreState): IScopeFromRedux => {
  const { map: assigneesIdMap, isFetched: assigneesFetched, error: assigneesError } = state.assignees;
  const activeSessionsIds = filterActiveSessions(state.sessions.items).map((session) => session.id);
  const { isFetched: sessionsFetched, error: sessionsError, map: sessionIdMap } = state.sessions;

  return {
    reduxStatesInitialized: assigneesFetched && sessionsFetched,
    reduxStateError: assigneesError || sessionsError,
    sessionIdMap,
    activeSessionsIds,
    assigneesIdMap,
    assignees: Object.values(assigneesIdMap),
  };
};

const PAGING_SIZE = 100;
export class ListCtrl implements IStateInit {
  private taskEvents: TaskEvents;
  private okrEvents: OkrEvents;
  private teamEvents: TeamEvents;
  private badgeEvents: BadgeEvents;
  private isListLoadedOnce: boolean;
  private columnResizeStartX = 0;
  private confirmDeleteModal: IModalServiceInstance;
  private allListRules = {};
  private filterRules: IListFilter[];
  private booleanOperator = "and";
  private isListTypeChanged: boolean;
  private isListTypeChangedInNonListView: boolean;
  private currentUserId: string;
  private notNeededIdsInAssigneesArr: string[] = [];
  private notNeededIdsInTeamsArr: string[] = [];
  private notNeededIdsInMembersArr: string[] = [];
  private notNeededIdsInManagersArr: string[] = [];
  private currentUserTeamMembersIds: string[] = [];
  private isDesignScoreGridEnabled = false;
  private isTheUserWhoCreatedEnabled = false;
  private favoritesManager: FavoritesManager;
  private readonly defaultSortingRule: IListSort = { fieldName: "attainment", reversed: true, sortOrder: 0 };
  private isInitialViewChange = true;

  public static $inject = [
    "$scope",
    "$stateParams",
    "$state",
    "$q",
    "GoalService",
    "MetricService",
    "ListService",
    "PermissionsService",
    "$uibModal",
    "CurrentUserService",
    "$rootScope",
    "UIErrorHandlingService",
    "AnalyticsService",
    "DocumentExporterService",
    "$ngRedux",
    "PlanningSessionsActions",
    "AssigneeActions",
    "FeatureToggleV2Service",
    "ViewHistoryService",
    "ChartSettingsLoader",
    "ChartSettingsModalOpener",
    "FavoritesManagerFactory",
  ];

  constructor(
    private $scope: IListScope,
    private $stateParams: IListStateParams,
    private $state: StateService,
    private $q: IQService,
    private goalService: GoalService,
    private metricService: MetricService,
    private listService: ListService,
    private permissionsService: PermissionsService,
    private $uibModal: IModalService,
    private currentUserService: CurrentUserService,
    private $rootScope: IGtmhubRootScopeService & IConfidenceSettingsRootScopeService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private analyticsService: AnalyticsService,
    private documentExporterService: DocumentExporterService,
    private $ngRedux: INgRedux,
    private planningSessionsActions: PlanningSessionsActions,
    private assigneeActions: AssigneeActions,
    private featureToggleService: FeatureToggleV2Service,
    private viewHistoryService: ViewHistoryService,
    private chartSettingsLoader: ChartSettingsLoader,
    private chartSettingsModalOpener: ChartSettingsModalOpener,
    private favoritesManagerFactory: FavoritesManagerFactory
  ) {
    if ($rootScope.newExperienceEnabledForUser) {
      this.favoritesManager = this.favoritesManagerFactory(["list"]);
      this.favoritesManager.initFavorites();
    }

    this.taskEvents = new TaskEvents(this.$scope);
    this.okrEvents = new OkrEvents(this.$scope);
    this.teamEvents = new TeamEvents(this.$scope);
    this.badgeEvents = new BadgeEvents(this.$scope);
  }

  public stateInit(): IPromise<unknown> {
    this.$scope.sizeOfGif = SizeOfGif;
    this.$scope.indicators = { initialLoad: true };
    this.$scope.screenName = this.$state.current.name;

    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);

    this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());

    return this.$scope
      .watchUntilAndRejectOnError(
        () => this.$scope.reduxStatesInitialized,
        () => this.$scope.reduxStateError
      )
      .then(
        () => this.init(),
        () => this.$scope.reduxStateError
      )
      .catch((err) => this.uiErrorHandlingService.handleModal(err));
  }

  private init = (): IPromise<unknown> => {
    this.$scope.colors = colors.listsColors;
    this.filterRules = [];
    this.$scope.currentPage = 1;
    this.$scope.columns = [];
    this.$scope.sortingRules = [];
    this.$scope.$on("$destroy", () => this.chartSettingsModalOpener.close());

    // resizing methods
    this.$scope.onColumnResizeStart = this.onColumnResizeStart;
    this.$scope.onColumnResizeEnd = this.onColumnResizeEnd;

    this.$scope.currentUserRestricted = userHasOnlyResctrictedRole(getCurrentUserRoles());

    const openFilter = this.$stateParams.isNewList;

    this.$scope.toggled = {
      filter: openFilter,
      columns: false,
      sort: false,
      permissions: false,
    };

    this.$scope.checkedColumnsMap = {};
    this.$scope.columnDragStart = this.columnDragStart;
    this.$scope.listOptions = ListOptions;
    this.$scope.sortingOptions = SortingOptions;
    this.$scope.listActions = ListActions;
    this.$scope.ruleDictionary = this.listService.getOperatorsDictionary();

    this.$scope.filtersChange = this.filtersChange;
    this.$scope.sortingChange = this.sortingChange;
    this.$scope.columnsChange = this.columnsChange;
    this.$scope.triggerNewView = this.triggerNewView;
    this.$scope.changeColumnOrder = this.changeColumnOrder;
    this.$scope.saveNewListRules = this.saveNewListRules;
    this.$scope.saveListAs = this.saveListAs;
    this.$scope.confirmDelete = this.confirmDelete;
    this.$scope.cloneList = this.cloneList;
    this.$scope.navListsIds = this.currentUserService.getUserSetting("listIds");
    this.$scope.changeListType = this.changeListType;
    this.$scope.onChangeListTitle = this.onChangeListTitle;
    this.$scope.onPageChange = this.onPageChange;
    this.$scope.changeListColor = this.changeListColor;
    this.$scope.openItem = this.openItem;
    this.$scope.getLatestSnapshotCustomFieldValue = getLatestSnapshotCustomFieldValue;
    this.$scope.exportToExcel = this.exportToExcel;
    this.$scope.onAccessChange = this.onAccessChange;
    this.$scope.onViewChange = this.onViewChange;

    this.taskEvents.onTaskCreated(() => this.reloadList());
    this.taskEvents.onTaskDeleted(() => this.reloadList());
    this.taskEvents.onTaskUpdated(() => this.reloadList());

    this.okrEvents.onGoalsChanged((args) => this.onGoalsChanged(args));

    this.teamEvents.onTeamCreated(() => this.reloadList());
    this.teamEvents.onTeamUpdated(() => this.reloadList());
    this.teamEvents.onTeamDeleted(() => this.reloadList());
    this.badgeEvents.onBadgeAwardChanged(() => this.reloadList());
    this.$scope.addToFavorites = this.addToFavorites;
    this.$scope.removeFromFavorites = this.removeFromFavorites;

    this.$scope.$on("goalDeleted", this.reloadList);
    this.$scope.$on("chartSettingsChange", this.onChartSettingsChange);

    this.currentUserId = getCurrentUserId();

    return this.$q
      .all([
        this.featureToggleService.isFeatureAvailable(FeatureFlag.FeatureCloseOKR),
        this.featureToggleService.isFeatureAvailable(FeatureFlag.ChartsInLists),
        this.featureToggleService.isFeatureAvailable(FeatureFlag.DesignScoreGrid),
        this.featureToggleService.isFeatureAvailable(FeatureFlag.ShowTheUserWhoCreated),
      ])
      .then(([isCloseOkrFeatureEnabled, isChartsInListsEnabled, isDesignScoreGridEnabled, isTheUserWhoCreatedEnabled]) => {
        this.$scope.isCloseOkrFeatureEnabled = isCloseOkrFeatureEnabled;
        this.$scope.isChartsInListsEnabled = isChartsInListsEnabled;
        this.isDesignScoreGridEnabled = isDesignScoreGridEnabled;
        this.isTheUserWhoCreatedEnabled = isTheUserWhoCreatedEnabled;
        this.getListOnInit();
      });
  };

  private addToFavorites = () => {
    this.favoritesManager?.addToFavorites(this.$scope.list as { id: string }, "list", this.getNavItem());
  };

  private removeFromFavorites = () => {
    this.favoritesManager?.removeFromFavorites(this.$scope.list as { id: string }, this.getNavItem());
  };

  private getNavItem = (): INavItem => {
    return this.favoritesManager?.getFavoriteItem({ id: this.$scope.list.id }) || navItemsFactory.buildListNavItem(this.$scope.list);
  };

  private openItem = (type: ListTypeKeys, item: { id: string }): void => {
    switch (type) {
      case "objective":
        this.$state.go("._goalSidebar.goal", { id: item.id });
        break;
      case "keyresult":
        this.$state.go("._metric.metric", { metricId: item.id });
        break;
      case "task":
        this.$state.go("._task.task", { taskId: item.id });
        break;
      case "employee":
        this.$state.go(".employee", { employeeId: item.id });
        break;
      case "team":
        this.$state.go(".team", { teamId: item.id });
        break;
    }
  };

  private onGoalsChanged = (args: GoalsChangedEventArgs): void => {
    switch (args.reason) {
      case "metricCreated":
      case "metricPatched":
      case "metricMoved":
      case "metricDeleted":
      case "metricUpdated":
      case "goalPatched":
      case "goalCloned":
      case "goalCreated":
      case "goalStatusChanged":
        this.reloadList();
        break;
    }
  };

  private changeListColor = (color: string): void => {
    this.updateList({ color: color }).then(() => {
      this.$rootScope.$broadcast("listUpdated", { ...this.$scope.list }, ListChangedEvent.Modified);
    });
  };

  private getListOnInit = (): IPromise<unknown> => {
    return this.listService.getList(this.$stateParams.listId).then(
      (list) => {
        this.$scope.list = list;
        this.$scope.list.typeKey = ListTypeKeysUI[this.$scope.list.type];
        this.$scope.isFavorite = () => this.favoritesManager?.entityIsFavorite({ id: list.id });

        this.filterRules = (this.$scope.list.filter && this.$scope.list.filter.ruleBounds) || [];
        this.$scope.columns = this.$scope.list.columns || [];
        this.$scope.columns = this.isTheUserWhoCreatedEnabled ? this.$scope.columns : this.$scope.columns.filter((x) => x.fieldName !== "createdById");
        this.$scope.sortingRules = this.$scope.list.sort || [];
        this.$scope.sortingRules = this.isTheUserWhoCreatedEnabled ? this.$scope.sortingRules : this.$scope.sortingRules.filter((x) => x.fieldName !== "createdById");
        if (!this.$scope.sortingRules.length) {
          this.$scope.sortingRules.push(this.defaultSortingRule);
        }

        this.viewHistoryService.addItemToViewHistory(
          this.viewHistoryService.payloadBuilder.withList({ id: this.$scope.list.id }).forSource("navigation").generate(),
          navItemsFactory.buildListNavItem(this.$scope.list)
        );

        return this.checkPermissions().then(() => {
          const initPromises: IPromise<unknown>[] = [];
          initPromises.push(this.getListPropertiesByType(ListPropertiesTypes[this.$scope.list.type]));

          const initialListOptions = this.generateListRulesObject(ListOptions.filter);
          initPromises.push(this.loadList(initialListOptions));

          return this.$q.all(initPromises).then(() => {
            this.setFiltersAndDefinitionsToScope();
            if (this.$scope.isChartsInListsEnabled) {
              this.setListView();
            }
          });
        });
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  };

  private setFiltersAndDefinitionsToScope = () => {
    const context: IListPropertyToGridFilterDefinitionContext = {
      confidenceScale: this.$rootScope.confidenceScale,
      confidenceSettings: this.$rootScope.confidenceSettings,
    };
    const properties = Object.values(this.$scope.properties);
    const filterDefinitions = properties
      .filter((p) => p.isFilterable)
      .map((p) => convertListPropertyToGridFilterDefinition(p, context))
      .filter((d) => !!d);
    this.$scope.filterDefinitions = toGridDefinitionMap(filterDefinitions);
    this.$scope.filters = convertListFiltersToGridFilters(this.filterRules, this.$scope.filterDefinitions, context);
    this.$scope.sortingDefinitions = toGridDefinitionMap(properties.filter((p) => p.isSortable) as IGridSortingDefinition[]);
    this.$scope.columnDefinitions = toGridDefinitionMap(properties.filter((p) => p.isSelectable) as IGridColumnDefinition[]);

    if (!this.isTheUserWhoCreatedEnabled) {
      delete this.$scope.sortingDefinitions.createdById;
      delete this.$scope.filterDefinitions.createdById;
      delete this.$scope.columnDefinitions.createdById;
    }
  };

  private setListView = () => {
    // TODO: set persisted view or the default one once backend persist is implemented and the view is returned as part of the list entity
    this.$scope.view = ListView.list;
    this.$scope.viewDisplayName = listViewDisplayNameMap.get(this.$scope.view);
    this.$scope.viewIcon = "table";
  };

  private reloadList = (): void => {
    this.loadList();
  };

  private fixGridWidth = (): void => {
    const totalWidth = this.getTotalColumnWidth();
    document.getElementById("list-grid-container").style.width = `${totalWidth}px`;
  };

  private getTotalColumnWidth = (): number => {
    let totalWidth = 0;
    for (let i = 0; i < this.$scope.columns.length; i++) {
      totalWidth = totalWidth + this.$scope.columns[i].width;
    }

    return totalWidth;
  };

  private onPageChange = (page: number) => {
    this.$scope.currentPage = page;
    this.$scope.reloadingList = true;
    this.loadList();
  };

  private generateFieldsInUseArray = (array: ListRuleArray): ListRuleArray => {
    return array.reduce((fieldsInUseArray, item) => (fieldsInUseArray.push(item.fieldName), fieldsInUseArray), []);
  };

  private assigneeFilterIndexesToEnrich = [];
  private sessionFilterIndexesToEnrich = [];
  private goalFilterIndexesToEnrich = [];
  private metricFilterIndexesToEnrich = [];
  private teamFilterIndexesToEnrich = [];
  private memberFilterIndexesToEnrich = [];
  private managerFilterIndexesToEnrich = [];

  private columnDragStart = (index: number) => {
    setTimeout(() => {
      const dndPlaceholderEl = document.querySelector<HTMLElement>(".list-row .dndPlaceholder");
      const headerEl = document.querySelectorAll<HTMLElement>(".list-col-header").item(index);
      if (dndPlaceholderEl && headerEl) {
        dndPlaceholderEl.style.width = `${headerEl.offsetWidth}px`;
      }
    }, 35);
  };

  private neededGoalIds = [];
  private neededMetricIds = [];
  private neededTeamIds = [];

  private processFilters = (neededMapsRequests): void => {
    this.filterRules.forEach((rule, ruleIndex) => {
      if (rule.fieldName === ListsPropertyNames.tags) {
        // that needs to be mapped to the format which the tagsinput library expects
        rule.viewValue = rule.value.map((eachTag) => {
          return { name: eachTag };
        });
      }

      if (rule.fieldName === ListsPropertyNames.attainment || rule.fieldName === ListsPropertyNames.progress || rule.fieldName === ListsPropertyNames.goalAttainment) {
        rule.viewValue = rule.value * 100;
      }

      if (rule.fieldName === ListsPropertyNames.sessionId || rule.fieldName === ListsPropertyNames.parentGoalSessionId) {
        this.sessionFilterIndexesToEnrich.push(ruleIndex);
      }

      if (rule.fieldName === ListsPropertyNames.goalId) {
        this.processFilterGoalId(neededMapsRequests, rule, ruleIndex);
      }

      if (rule.fieldName === ListsPropertyNames.parentId) {
        this.processFilterParentId(neededMapsRequests, rule, ruleIndex);
      }

      if (
        rule.fieldName === ListsPropertyNames.ownerId ||
        rule.fieldName === ListsPropertyNames.ownerIds ||
        rule.fieldName === ListsPropertyNames.createdById ||
        rule.dataType === ListFilterTypes.selectassignee ||
        rule.fieldName === ListsPropertyNames.goalOwnerId ||
        rule.fieldName === ListsPropertyNames.goalOwnerIds
      ) {
        this.assigneeFilterIndexesToEnrich.push(ruleIndex);
      }

      if (rule.fieldName === ListsPropertyNames.teamIds) {
        this.processFilterTeamId(neededMapsRequests, rule, ruleIndex);
      }

      if (rule.fieldName === ListsPropertyNames.members) {
        this.memberFilterIndexesToEnrich.push(ruleIndex);
        this.currentUserTeamMembersIds = [this.currentUserId];
      }

      if (rule.fieldName === ListsPropertyNames.manager) {
        this.managerFilterIndexesToEnrich.push(ruleIndex);
      }
    });
  };

  private processFilterGoalId = (neededMapsRequests, rule: IListFilter, ruleIndex: number) => {
    if (!neededMapsRequests.includes(this.getGoals)) {
      neededMapsRequests.push(this.getGoals);
    }
    this.neededGoalIds = [...this.neededGoalIds, ...rule.value];
    this.goalFilterIndexesToEnrich.push(ruleIndex);
  };

  private processFilterParentId = (neededMapsRequests, rule: IListFilter, ruleIndex: number): void => {
    if (!neededMapsRequests.includes(this.getGoals)) {
      neededMapsRequests.push(this.getGoals);
    }
    this.neededGoalIds = [...this.neededGoalIds, ...rule.value];
    this.goalFilterIndexesToEnrich.push(ruleIndex);

    if (this.isParentMetricAllowed()) {
      if (!neededMapsRequests.includes(this.getMetrics)) {
        neededMapsRequests.push(this.getMetrics);
      }
      this.neededMetricIds = [...this.neededMetricIds, ...rule.value];
      this.metricFilterIndexesToEnrich.push(ruleIndex);
    }
  };

  private processFilterTeamId = (neededMapsRequests, rule: IListFilter, ruleIndex: number) => {
    if (rule.value) {
      this.neededTeamIds = [...this.neededTeamIds, rule.value];
    }

    if (!neededMapsRequests.includes(this.getTeams)) {
      neededMapsRequests.push(this.getTeams);
    }

    this.teamFilterIndexesToEnrich.push(ruleIndex);
  };

  private processColumns = (neededMapsRequests): string[] => {
    let neededFieldsForGoalMap = [];

    this.$scope.columns.forEach((column) => {
      if (column.fieldName === ListsPropertyNames.goalId) {
        if (!this.neededGoalIds.length) {
          neededFieldsForGoalMap = [...neededFieldsForGoalMap, column.fieldName];
        }

        if (!neededMapsRequests.includes(this.getGoals)) {
          neededMapsRequests.push(this.getGoals);
        }
      }

      if (this.shouldFetchParentId(column)) {
        if (!this.neededGoalIds.length) {
          neededFieldsForGoalMap = [...neededFieldsForGoalMap, ListsPropertyNames.parentId];
        }

        if (!neededMapsRequests.includes(this.getGoals)) {
          neededMapsRequests.push(this.getGoals);
        }

        if (!neededMapsRequests.includes(this.getMetrics) && this.isParentMetricAllowed()) {
          neededMapsRequests.push(this.getMetrics);
        }
      }

      if (column.fieldName === ListsPropertyNames.teamIds && !neededMapsRequests.includes(this.getTeams)) {
        neededMapsRequests.push(this.getTeams);
      }
    });

    return neededFieldsForGoalMap;
  };

  private shouldFetchParentId = (column: IListColumn) => {
    return column.fieldName === ListsPropertyNames.parentId || column.fieldName === ListsPropertyNames.closedStatus;
  };

  private getNeededMaps = (items?): void => {
    this.$scope.goalsIdMap = {};
    const neededMapsRequests = [];
    this.processFilters(neededMapsRequests);
    this.enrichSessionsFilterView();
    this.enrichAssigneesFilterView();
    this.enrichMembersFilterView();
    this.enrichManagersFilterView();

    const neededFieldsForGoalMap = this.processColumns(neededMapsRequests);

    items.forEach((item) => {
      neededFieldsForGoalMap.forEach((field) => {
        // we have parentType when the field closedStatus is requested and we don't have parentType when we need field id because of Linked to
        if ((field === ListsPropertyNames.parentId && (item.parentType === "goal" || !item.parentType)) || field !== ListsPropertyNames.parentId) {
          this.neededGoalIds.push(item[field]);
        }

        // we have parentType when the field closedStatus is requested and we don't have parentType when we need field id because of Linked to
        if (field === ListsPropertyNames.parentId && this.isParentMetricAllowed() && (item.parentType === "metric" || !item.parentType)) {
          this.neededMetricIds.push(item[field]);
        }
      });

      if (item[ListsPropertyNames.teamIds]) {
        this.neededTeamIds.push(item[ListsPropertyNames.teamIds]);
      }
    });

    this.neededGoalIds = this.neededGoalIds.filter((goalId, index, array) => goalId && array.indexOf(goalId) === index);
    this.neededMetricIds = this.neededMetricIds.filter((metricId, index, array) => metricId && array.indexOf(metricId) === index);
    this.neededTeamIds = unique(flatten(this.neededTeamIds));

    neededMapsRequests.forEach((request) => request());
  };

  private getGoals = (): void => {
    if (!this.neededGoalIds.length) {
      return;
    }

    this.goalService
      .getGoalsV2<IGoalList>({
        fields: this.$scope.isCloseOkrFeatureEnabled ? "id,name,ownerIds,closedStatus" : "id,name,ownerIds",
        filter: {
          _id: { $in: this.neededGoalIds },
        },
      })
      .then((goalsCollection) => {
        goalsCollection.items.forEach((goal) => {
          this.$scope.goalsIdMap[goal.id] = {
            id: goal.id,
            name: goal.name,
            type: "objective",
            closedStatus: goal.closedStatus,
          };
        });

        this.goalFilterIndexesToEnrich.forEach((index) => {
          const rule = this.filterRules[index];
          rule.viewValue = rule.value?.reduce((enrichedGoals: IOKRFilterView[], id: string) => {
            const enrichedGoal = this.$scope.goalsIdMap[id];
            return enrichedGoal ? [...enrichedGoals, enrichedGoal] : enrichedGoals;
          }, []);
        });

        this.goalFilterIndexesToEnrich = [];
        this.neededGoalIds = [];
      });
  };

  private getMetrics = (): void => {
    if (!this.neededMetricIds.length) {
      return;
    }

    this.metricService
      .getMetricsV2({
        fields: this.$scope.isCloseOkrFeatureEnabled ? "id,name,goalClosedStatus" : "id,name",
        filter: {
          _id: { $in: this.neededMetricIds },
        },
      })
      .then((metricsCollection) => {
        metricsCollection.items.forEach((metric) => {
          this.$scope.goalsIdMap[metric.id] = {
            id: metric.id,
            name: metric.name,
            type: "keyresult",
            closedStatus: metric.goalClosedStatus,
          };
        });

        this.metricFilterIndexesToEnrich.forEach((index) => {
          const rule = this.filterRules[index];
          rule.viewValue = rule.value?.reduce((enrichedGoals: IOKRFilterView[], id: string) => {
            const enrichedMetric = this.$scope.goalsIdMap[id];
            return enrichedMetric ? [...enrichedGoals, enrichedMetric] : enrichedGoals;
          }, []);
        });

        this.metricFilterIndexesToEnrich = [];
        this.neededMetricIds = [];
      });
  };

  private isParentMetricAllowed = (): boolean => {
    return this.$scope.list.type === ListTypes.Objective || this.$scope.list.type === ListTypes.Task;
  };

  private getTeams = (): void => {
    this.$scope.teamsIdMap = toIdMap(this.$scope.assignees.filter((assignee) => this.neededTeamIds.indexOf(assignee.id) !== -1 && assignee.type === "team"));
    this.neededTeamIds = [];
    this.enrichTeamsFilterView();
  };

  private enrichAssignee = (id: string, dynamicValueType?: DynamicValueType): IAssigneeFilterView => {
    const assignee = this.$scope.assigneesIdMap[id];
    if (assignee) {
      let name;
      if (assignee.type === "user") {
        name = !dynamicValueType ? assignee.name : `${localize("me")} (${localize("current_user")})`;
      }

      if (assignee.type === "team") {
        name = !dynamicValueType ? assignee.name : assignee.name + " " + localize("plus_its_members");
      }

      return {
        id,
        name,
        picture: assignee.picture,
        email: assignee.email,
        type: assignee.type,
        color: assignee.color,
        ...(dynamicValueType && { dynamicValueType }),
        trackKey: id + "-" + name,
      };
    }
  };

  private enrichTeam = (id: string): ITeamFilterView => {
    return (
      this.$scope.teamsIdMap[id] && {
        ...this.$scope.teamsIdMap[id],
        trackKey: id + "-" + this.$scope.teamsIdMap[id].name,
      }
    );
  };

  private enrichMembersFilterView = (): void => {
    this.memberFilterIndexesToEnrich.forEach((index) => {
      const rule = this.filterRules[index];
      if (!rule.value) {
        rule.value = [];
      }

      rule.viewValue = rule.value.reduce((acc, ruleValue) => {
        const enrichedMember = this.enrichAssignee(ruleValue);
        acc = enrichedMember ? [...acc, enrichedMember] : acc;

        return acc;
      }, []);

      if (rule.fieldName === ListsPropertyNames.members && rule.dynamicFilters) {
        rule.dynamicFilters.forEach((dynamicFilter) => {
          if (dynamicFilter.dynamicValueType === DynamicFilters.currentUserTeamsMember) {
            if (!dynamicFilter.value) {
              dynamicFilter.value = this.currentUserTeamMembersIds;
            }
            dynamicFilter.value.forEach((memberId) => {
              this.notNeededIdsInMembersArr = [...this.notNeededIdsInMembersArr, memberId];
              const enrichedMember = this.enrichAssignee(memberId, DynamicFilters.currentUserTeamsMember);
              rule.viewValue = enrichedMember ? [...rule.viewValue, enrichedMember] : rule.viewValue;
              this.currentUserTeamMembersIds = [...dynamicFilter.value];
            });
          }
        });
      }
    });

    this.memberFilterIndexesToEnrich = [];
  };

  private setViewValueToAssigneeFilter = (rule: IListFilter) => {
    rule.viewValue = rule.value.reduce((acc, ruleValue) => {
      const enrichedAssignee = this.enrichAssignee(ruleValue);
      acc = enrichedAssignee ? [...acc, enrichedAssignee] : acc;

      return acc;
    }, []);
  };

  private enrichManagersFilterView = (): void => {
    this.managerFilterIndexesToEnrich.forEach((index) => {
      const rule = this.filterRules[index];
      if (!rule.value) {
        rule.value = [];
      }

      this.setViewValueToAssigneeFilter(rule);

      if (rule.fieldName === ListsPropertyNames.manager && rule.dynamicFilters) {
        rule.dynamicFilters.forEach((dynamicFilter) => {
          if (dynamicFilter.dynamicValueType === DynamicFilters.currentUserTeamsManager) {
            dynamicFilter.value.forEach((managerId) => {
              const enrichedCurrentUserTeamsManager = this.enrichAssignee(dynamicFilter.value[0], DynamicFilters.currentUserTeamsManager);
              rule.viewValue = enrichedCurrentUserTeamsManager ? [...rule.viewValue, enrichedCurrentUserTeamsManager] : rule.viewValue;
              this.notNeededIdsInManagersArr = [...this.notNeededIdsInManagersArr, managerId];
            });
          }
        });
      }
    });

    this.managerFilterIndexesToEnrich = [];
  };

  private enrichAssigneesFilterView = (): void => {
    this.assigneeFilterIndexesToEnrich.forEach((index) => {
      const rule = this.filterRules[index];
      if (!rule.value) {
        rule.value = [];
      }

      this.setViewValueToAssigneeFilter(rule);

      if (rule.dynamicFilters) {
        rule.dynamicFilters.forEach((dynamicFilter) => {
          let enrichedCurrentUser: IAssigneeFilterView;
          switch (dynamicFilter.dynamicValueType) {
            case DynamicFilters.currentUser:
              enrichedCurrentUser = this.enrichAssignee(dynamicFilter.value[0], DynamicFilters.currentUser);
              rule.viewValue = enrichedCurrentUser ? [...rule.viewValue, enrichedCurrentUser] : rule.viewValue;
              break;

            case DynamicFilters.teamPlusMembers:
            case DynamicFilters.teamOrg:
              dynamicFilter.value.forEach((teamId) => {
                this.notNeededIdsInAssigneesArr = [...this.notNeededIdsInAssigneesArr, teamId];
                const enrichedTeam = this.enrichAssignee(teamId, dynamicFilter.dynamicValueType);
                rule.viewValue = enrichedTeam ? [...rule.viewValue, enrichedTeam] : rule.viewValue;
              });
              break;
          }
        });
      }
    });

    this.assigneeFilterIndexesToEnrich = [];
  };

  private enrichTeamsFilterView = (): void => {
    this.teamFilterIndexesToEnrich.forEach((index) => {
      const rule = this.filterRules[index];
      if (!rule.value) {
        rule.value = [];
      }

      rule.viewValue = rule.value?.reduce((enrichedTeams: ITeamFilterView[], id: string) => {
        const enrichedTeam = this.enrichTeam(id);
        return enrichedTeam ? [...enrichedTeams, enrichedTeam] : enrichedTeams;
      }, []);

      if (rule.fieldName === ListsPropertyNames.teamIds && rule.dynamicFilters) {
        rule.dynamicFilters.forEach((dynamicFilter) => {
          if (dynamicFilter.dynamicValueType === DynamicFilters.currentUserTeams) {
            const enrichedTeam = {
              name: localize("my_teams"),
              dynamicValueType: dynamicFilter.dynamicValueType,
              value: dynamicFilter.value,
              trackKey: localize("my_teams"),
            };
            rule.viewValue = [...(rule.viewValue ? rule.viewValue : []), enrichedTeam];
            if (dynamicFilter.value) {
              this.notNeededIdsInTeamsArr = [...this.notNeededIdsInTeamsArr, ...dynamicFilter.value];
            }
          }
        });
      }
    });

    this.teamFilterIndexesToEnrich = [];
  };

  private getListPropertiesByType = (targetType: string): IPromise<unknown> => {
    return this.listService.getListPropertiesByType(targetType).then(
      (listProperties) => {
        let allProperties = [...listProperties.fields, ...listProperties.customFieldsProperties];

        if (!this.isDesignScoreGridEnabled) {
          allProperties = allProperties.filter((prop) => prop.fieldName !== "designScore.totalPoints");
        }

        this.$scope.selectableListProperties = allProperties.filter((p) => p.isSelectable);
        this.$scope.properties = this.generateMap(allProperties, "fieldName");
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  };

  private enrichSessionsFilterView = (): void => {
    this.sessionFilterIndexesToEnrich.forEach((index) => {
      const rule = this.filterRules[index];

      if (!rule.value) {
        rule.value = [];
      }

      rule.viewValue = rule.value.reduce((enrichedSessions: ISessionFilterView[], id: string) => {
        const enrichedSession = this.enrichSession(id);
        return enrichedSession ? [...enrichedSessions, enrichedSession] : enrichedSessions;
      }, []);

      if (!this.filterRules[index].viewValue) {
        this.filterRules[index].viewValue = [];
      }

      if (rule.dynamicFilters) {
        rule.dynamicFilters.forEach((dynamicFilter) => {
          const enrichedSession = {
            title: localize("active_sessions"),
            dynamicValueType: dynamicFilter.dynamicValueType,
            value: dynamicFilter.value,
          };

          if (dynamicFilter.dynamicValueType === DynamicFilters.currentSessions) {
            rule.viewValue = [...rule.viewValue, enrichedSession];
          }
        });
      }
    });

    this.sessionFilterIndexesToEnrich = [];
  };

  private enrichSession = (id: string): ISessionFilterView => {
    return this.$scope.sessionIdMap[id] && { id, title: this.$scope.sessionIdMap[id].title };
  };

  private filtersChange = (change: IGridFiltersChange): void => {
    if (this.$scope.isChartsInListsEnabled && this.$scope.view !== ListView.list && this.isListTypeChangedInNonListView) {
      this.isListTypeChangedInNonListView = false;
      return;
    }

    // listItems are loaded before we set the filters
    // since on init we have the persisted list filters in the list
    // and the conversion between list filter rules and grid filters is unecessary
    if (change.initial && !this.$scope.isChartsInListsEnabled) {
      return;
    }

    const context: IGridFilterToListFilterContext = {
      confidenceSettings: this.$rootScope.confidenceSettings,
    };

    // filters are needed for chart settings
    this.filterRules = convertGridFiltersToListFilters(this.$scope.filters, this.$scope.properties, context);
    this.$scope.filtersForChartSettings = {
      booleanOperator: this.booleanOperator,
      ruleBounds: this.removeEmptyFilterValues(),
    };

    // we should trigger new list view only on not initial change - see the comment for change.initial case
    if (!change.initial) {
      this.$scope.currentPage = 1;
      this.$scope.reloadingList = true;
      this.triggerNewView(this.$scope.listActions.changeFilters, ListOptions.filter);
      if (this.$scope.isChartsInListsEnabled) {
        this.$rootScope.$broadcast("listFiltersChange", this.$scope.filtersForChartSettings);
      }
    }
  };

  private sortingChange = () => {
    this.triggerNewView(ListActions.editSorting, ListOptions.sort);
  };

  private columnsChange = () => {
    for (const column of this.$scope.columns) {
      column.width = 200;
      column.type = this.$scope.properties[column.fieldName].dataType;
    }

    if (this.$scope.columns.length === Object.keys(this.$scope.checkedColumnsMap).length) {
      this.changeColumnOrder();
    } else {
      for (const column of this.$scope.columns) {
        if (!(column.fieldName in this.$scope.checkedColumnsMap)) {
          column.isNew = true;
        }
      }

      this.$scope.checkedColumnsMap = this.generateMap(this.$scope.columns, "fieldName");
      this.$scope.editMode = true;

      this.triggerNewView(ListActions.changeColumns, ListOptions.columns);
      this.fixGridWidth();
    }
  };

  private triggerNewView = (action: ListActions, listOption?: ListOptions, rule?): void => {
    if (rule) {
      if (rule.fieldName === ListsPropertyNames.attainment || rule.fieldName === ListsPropertyNames.progress || rule.fieldName === ListsPropertyNames.goalAttainment) {
        if (rule.viewValue || rule.viewValue === 0) {
          rule.value = rule.viewValue / 100;
        } else {
          rule.value = rule.viewValue;
        }
      }

      const isValueTruthy = this.checkForTruthyValue(rule);

      if (isValueTruthy) {
        if (rule.dataType === "date" && (rule.operator === "isBetween" || rule.operator === "isNotBetween")) {
          if (rule.value.startDate && rule.value.endDate) {
            this.generateListRulesObject(listOption);
            this.loadList();
          } else {
            return;
          }
        } else {
          this.generateListRulesObject(listOption);
          this.loadList();
        }
      }

      if (action !== "reorderColumns" && action !== "removeColumn" && action !== "addColumn" && isValueTruthy) {
        this.$scope.reloadingList = true;
      }

      if (action === this.$scope.listActions.editFilter && !this.$scope.editMode && isValueTruthy) {
        this.$scope.editMode = true;
      }
    }

    if (action !== this.$scope.listActions.editFilter) {
      if (this.isListLoadedOnce) {
        this.$scope.editMode = true;
      }

      this.generateListRulesObject(listOption);
      this.loadList();
    }
  };

  private unmarkNewColumns = () => {
    for (let i = 0; i < this.$scope.columns.length; i++) {
      delete this.$scope.columns[i].isNew;
    }
  };

  private loadList = (listOptions?: ILoadList): IPromise<unknown> => {
    if (!listOptions) {
      listOptions = this.allListRules;
    }

    listOptions.skip = (this.$scope.currentPage - 1) * PAGING_SIZE;
    listOptions.limit = PAGING_SIZE;

    return this.listService.loadList({ id: this.$stateParams.listId, body: listOptions }).then(
      (listItemsCollection) => {
        if (this.$scope.list.type === ListTypes.Objective) {
          this.$scope.listItems = this.goalService.applyColoringToGoalCollection(listItemsCollection as ICollection<IGoal>).items;
        } else {
          this.$scope.listItems = listItemsCollection.items;
        }

        this.$scope.reloadingList = false;
        this.$scope.indicators.initialLoad = false;
        this.$scope.checkedColumnsMap = this.generateMap(this.$scope.columns, "fieldName");
        this.$scope.sortingFieldsInUseArray = this.generateFieldsInUseArray(this.$scope.sortingRules);
        this.$scope.propertiesDictionary = this.listService.getDictionaryByListType(ListPropertiesTypes[this.$scope.list.type]);

        this.isListLoadedOnce = true;

        this.getNeededMaps(listItemsCollection.items);
        this.unmarkNewColumns();
        this.fixGridWidth();

        setTimeout(() => {
          const listGridContainerEl = document.getElementById("list-grid-container");
          const vlEl = document.querySelector<HTMLElement>(".vl");
          if (vlEl && listGridContainerEl) {
            vlEl.style.height = `${listGridContainerEl.clientHeight - 38}px`;
          }

          const listGridWrapperEl = document.querySelector(".list-grid-wrapper");
          if (listGridWrapperEl) {
            listGridWrapperEl.scrollTo({ top: 0 });
          }
        }, 35);

        this.$scope.totalPages = Math.ceil(listItemsCollection.totalCount / PAGING_SIZE);
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  };

  private getColumnsForRequest = (): IListColumn[] => {
    if (
      // this is needed for fetching the task parent goal/metric so we can retrieve the closedStatus from it
      this.$scope.list.type === ListTypes.Task &&
      this.$scope.columns.find((c) => c.fieldName === "closedStatus") &&
      !this.$scope.columns.find((c) => c.fieldName === "parentId")
    ) {
      return [...this.$scope.columns, { fieldName: ListsPropertyNames.parentId, type: ListFilterTypes.id, width: 250 }];
    } else {
      return this.$scope.columns;
    }
  };

  private getChartSettingsForSave = (): ListChartSettings => {
    if (!this.$scope.isChartsInListsEnabled) {
      return null;
    } else {
      // the settings will be taken from the changed charts settings if any or from the list otherwise
      if (this.chartSettingsLoader.chartSettings) {
        return this.chartSettingsLoader.formatChartSettingsForSave();
      } else if (this.$scope.list.chartSettings) {
        return this.$scope.list.chartSettings;
      }
    }
  };

  private generateListRulesObject = (listOption?: ListOptions): ILoadList => {
    const options = {
      [ListOptions.columns]: this.getColumnsForRequest(),
      [ListOptions.filter]: {
        booleanOperator: this.booleanOperator,
        ruleBounds: this.removeEmptyFilterValues(),
      },
      [ListOptions.sort]: this.$scope.sortingRules.map(toggleClosedStatusSort),
      [ListOptions.type]: this.$scope.list.type,
      [ListOptions.view]: this.$scope.isChartsInListsEnabled ? this.$scope.view : null,
      [ListOptions.chartSettings]: this.getChartSettingsForSave(),
    };

    if (listOption) {
      this.allListRules[listOption] = options[listOption];
    }

    return options;
  };

  private saveNewListRules = (): void => {
    this.updateList(this.allListRules);
  };

  private saveListAs = (): void => {
    this.$uibModal.open({
      windowClass: "save-list-as",
      backdropClass: "dark-backdrop",
      component: "saveListAs",
      resolve: {
        listType: () => this.$scope.list.type,
        filters: () => this.$scope.filtersForChartSettings,
        view: () => this.$scope.view,
        chartSettings: () => this.getChartSettingsForSave(),
      },
    });
  };

  private changeColumnOrder = (): void => {
    this.triggerNewView(ListActions.reorderColumns, ListOptions.columns);
  };

  private updateList = (params: Partial<IList>, columnsResized?: boolean) => {
    return this.listService.updateList({ id: this.$stateParams.listId, params }).then(
      (updatedList: IList) => {
        if (params.access || params.title || params.color) {
          return;
        }

        if (!columnsResized) {
          this.$scope.editMode = false;
          this.allListRules = {};
        }

        if (this.isListTypeChanged) {
          this.isListTypeChanged = false;
        }
        this.$scope.list = updatedList;
        this.$scope.list.typeKey = this.$scope.list.type.toLowerCase().replace(/\s/g, "");
        // indicator will be stopped here

        this.$rootScope.$broadcast("listFiltersUpdated");
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  };

  private deleteList = (): void => {
    this.listService.deleteList({ id: this.$stateParams.listId }).then(
      () => {
        const list = this.$scope.list;

        if (this.$scope.navListsIds.includes(list.id)) {
          this.$scope.navListsIds = this.$scope.navListsIds.filter((id) => id !== list.id);
          this.currentUserService.setUserSetting({ listIds: this.$scope.navListsIds });
          this.$rootScope.$broadcast("listDeleted", list, ListChangedEvent.Deleted);
        }
        this.confirmDeleteModal.close();

        this.$state.go("gtmhub.lists");
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  };

  private onAccessChange = (access: IAccess): void => {
    if (this.$scope.allowedActions.includes("modifyPermissions")) {
      this.updateList({ access });
    }
  };

  // triggered from the view only when isChartsInListsEnabled === true
  private onViewChange = (view: ListView, viewIcon: string): void => {
    const previousView = this.$scope.view;
    this.$scope.view = view;
    this.$scope.viewIcon = viewIcon;
    this.$scope.viewDisplayName = listViewDisplayNameMap.get(this.$scope.view);
    this.$scope.editMode = true;
    this.generateListRulesObject(ListOptions.view);

    if (this.isInitialViewChange) {
      this.chartSettingsLoader.init();
      this.isInitialViewChange = false;
    } else {
      this.$rootScope.$broadcast("listViewChange", view);
    }

    if (view === ListView.list) {
      this.chartSettingsModalOpener.close();
    }

    if (view !== ListView.list && previousView === ListView.list) {
      this.chartSettingsModalOpener.open(this.$scope.selectableListProperties);
    }
  };

  private onChartSettingsChange = (): void => {
    this.generateListRulesObject(ListOptions.chartSettings);
    this.$scope.editMode = true;
  };

  private checkPermissions = (): IPromise<unknown> => {
    return this.permissionsService.getCurrentUserAllowedActionsPerItem(this.$stateParams.listId, "list").then(
      (actions: IUserItemAllowedActions) => {
        this.$scope.allowedActions = actions.currentUserActions;
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  };

  private generateMap = (items, parameter) => {
    return items.reduce((map, item) => (map[item[parameter]] ? map : ((map[item[parameter]] = item), map)), {});
  };

  private confirmDelete = () => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.confirmDeleteModal = this.$uibModal.open({
      templateUrl: require("../views/confirm-delete.jade"),
      windowClass: "medium-small",
      controller: [
        "$scope",
        function ($scope: IConfirmDeleteScope) {
          $scope.confirm = () => {
            ctrl.deleteList();
          };
        },
      ],
    });
  };

  private cloneList = () => {
    this.listService.cloneList({ id: this.$stateParams.listId }).then(
      (clonedList) => {
        this.$state.go("gtmhub.list", { listId: clonedList.id, isNewList: true });
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  };

  private changeListType = (type: ListTypes): void => {
    const oldListType = this.$scope.list.type;

    this.isListTypeChanged = true;
    if (this.$scope.isChartsInListsEnabled) {
      this.isListTypeChangedInNonListView = true;
    }
    this.notNeededIdsInAssigneesArr = [];
    this.notNeededIdsInManagersArr = [];
    this.notNeededIdsInMembersArr = [];
    this.notNeededIdsInTeamsArr = [];

    this.$scope.editMode = true;
    this.$scope.list.type = type;
    this.$scope.list.typeKey = type.toLowerCase().replace(/\s/g, "");
    this.$scope.reloadingList = true;

    this.getListPropertiesByType(ListPropertiesTypes[type]).then(() => {
      this.processNewListRules();
      this.setFiltersAndDefinitionsToScope();
      this.allListRules = this.generateListRulesObject();
      if (this.$scope.isChartsInListsEnabled) {
        this.$rootScope.$broadcast("listTypeChange", {
          listType: this.$scope.list.type,
          listProperties: this.$scope.selectableListProperties,
          filters: this.$scope.filtersForChartSettings,
        });
      }
      this.loadList();
    });

    this.analyticsService.track("List Type Changed", {
      old_list_type: oldListType,
      new_list_type: type,
      created_at: this.$scope.list.createdAt,
      owner_id: this.$scope.list.ownerId,
      title: this.$scope.list.title,
      list_id: this.$scope.list.id,
      list_type: this.$scope.list.type,
    });
  };

  private processNewListRules = (): void => {
    const predefinedRules = this.listService.getPredefinedListRules(this.$scope.list.type, this.$scope.isCloseOkrFeatureEnabled);

    this.filterRules = predefinedRules.predefinedFilterRule;
    this.$scope.filtersForChartSettings = {
      booleanOperator: this.booleanOperator,
      ruleBounds: this.removeEmptyFilterValues(),
    };

    this.$scope.sortingRules = predefinedRules.predefinedSortingRule;
    this.$scope.columns = predefinedRules.predefinedColumns;
  };

  private onColumnResizeStart = ($event: DragEvent): void => {
    this.columnResizeStartX = $event.screenX;
  };

  private onColumnResizeEnd = ($event: DragEvent, column: IListColumn): void => {
    const widthDifference = $event.screenX - this.columnResizeStartX;
    const newWidth = column.width + widthDifference;
    const minColumnWidth = 40;

    column.width = newWidth < minColumnWidth ? minColumnWidth : Math.round(newWidth);

    this.fixGridWidth();
    if (!this.isListTypeChanged) {
      this.updateList({ columns: this.$scope.columns }, true);
    }
  };

  private onChangeListTitle = (): void => {
    this.updateList({ title: this.$scope.list.title }).then(() => {
      this.$rootScope.$broadcast("listUpdated", { ...this.$scope.list }, ListChangedEvent.Modified);
    });
  };

  private removeEmptyFilterValues = (): IListFilter[] => {
    return this.filterRules.filter((rule) => {
      if (!Array.isArray(rule.value)) {
        const isValueTruthy = this.checkForTruthyValue(rule);
        if (isValueTruthy) {
          return rule;
        }

        if (!isValueTruthy && rule.dynamicFilters?.length) {
          return rule;
        }
      } else {
        if (rule.value.length || rule.dynamicFilters?.length) {
          return rule;
        }
      }
    });
  };

  private checkForTruthyValue = (rule: IListFilter): boolean => {
    return rule.value || rule.value === 0;
  };

  private exportToExcel = (): void => {
    const listType = this.$scope.list.type;
    let propertiesDictionary: Record<string, string>;

    switch (listType) {
      case "Objective":
        propertiesDictionary = this.listService.goalPropertiesDictionary();
        break;
      case "Key Result":
        propertiesDictionary = this.listService.metricPropertiesDictionary();
        break;
      case "Task":
        propertiesDictionary = this.listService.taskPropertiesDictionary();
        break;
      case "Employee":
        propertiesDictionary = this.listService.employeePropertiesDictionary();
        break;
      case "Team":
        propertiesDictionary = this.listService.teamPropertiesDictionary();
        break;
    }

    const listColumns: IListColumnExcel[] = this.constructListColumnsExcelPayload(propertiesDictionary);

    const body: IExportListToExcelBody = {
      listTitle: this.$scope.list.title,
      listId: this.$scope.list.id,
      listType: listType,
      columns: listColumns,
      filter: { booleanOperator: "and", ruleBounds: this.removeEmptyFilterValues() },
      sort: this.$scope.sortingRules,
    };

    this.documentExporterService.exportListToExcel(body).then(
      (xlsxFileResponse) => {
        const generatedExcel = new Blob([xlsxFileResponse], { type: "application/octet-stream" });
        triggerFileDownload(generatedExcel, this.$scope.list.title + "_" + new Date().toLocaleDateString() + ".xlsx");
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  };

  private constructListColumnsExcelPayload = (propertiesDictionary: { [field: string]: string }): IListColumnExcel[] => {
    if (propertiesDictionary) {
      return this.$scope.columns.reduce((listColumns, current) => {
        const title = propertiesDictionary[current.fieldName];
        if (isCustomFieldColumn(current) || title) {
          listColumns.push({ name: current.fieldName, title: title });
        }
        return listColumns;
      }, []);
    }

    return [];
  };
}
